import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { NewUserDetailsProvider } from '@99designs/apps/spa/pages/auth/SSO/NewUserDetailsProvider';
import { SSONewUserDetails, UserRole } from '@99designs/apps/spa/pages/auth/SSO/common/types';
import {
    CreateAccountUserRole,
    useCreateAccount,
} from '@99designs/apps/spa/pages/auth/SSO/hooks/useCreateAccount';
import { Box, Button, ErrorAlert, Heading } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { HeadingWithBody, TileSelect } from '@99designs/spa-common';
import PageLayout from '../../PageLayout';
import { CreateAccountErrorMessage } from '../CreateAccountErrorMessage';
import { MarketingOptInInformationCard } from '../common/MarketingOptIn/MarketingOptInInformationCard';
import { PrivacyPolicyStatement } from '../common/PrivacyPolicyStatement';

interface NewUserPageProps {
    newUserDetails: SSONewUserDetails;
}

const maxWidth = '400px';

const CenterStack = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
`;

const ContinueButtonContainer = styled(Box)`
    width: ${maxWidth};

    ${mediaQuery.only('small')} {
        width: 100%;
    }
`;

type Form = {
    marketingOptedIn: boolean;
    role: CreateAccountUserRole;
};

export function NewUserByRoleSelectionPage({ newUserDetails }: NewUserPageProps) {
    const showMarketingOptIn = newUserDetails.requires_marketing_opt_in;

    const { register, handleSubmit, watch, control, setValue } = useForm<Form>({
        defaultValues: { role: undefined, marketingOptedIn: false },
    });

    const { createAccount, loading, serverError } = useCreateAccount(newUserDetails.target);

    const createUser = (data: Form) => {
        createAccount({
            role: data.role,
            source: newUserDetails.source,
            marketing_opted_in: showMarketingOptIn ? data.marketingOptedIn : true,
        });
    };

    return (
        <PageLayout maxWidth={'100vw'} showFooter={false}>
            <form onSubmit={handleSubmit(createUser)}>
                <CenterStack>
                    <Box maxWidth={maxWidth} paddingBottom="2bu">
                        <Heading appearanceLevel={2} accessibilityLevel={2}>
                            {__('Let’s begin your journey with 99designs')}
                        </Heading>
                    </Box>

                    <Box maxWidth={maxWidth} paddingBottom="2bu">
                        {serverError && (
                            <ErrorAlert data-testid="error-message">
                                <CreateAccountErrorMessage
                                    errorCode={serverError}
                                    onRetry={handleSubmit(createUser)}
                                />
                            </ErrorAlert>
                        )}
                    </Box>

                    <Box width="100%" paddingBottom="4bu" maxWidth="900px">
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <TileSelect<CreateAccountUserRole>
                                    options={[
                                        {
                                            label: (
                                                <HeadingWithBody
                                                    heading={__('I need something designed')}
                                                />
                                            ),
                                            value: UserRole.Customer,
                                        },
                                        {
                                            label: (
                                                <HeadingWithBody heading={__('I’m a designer')} />
                                            ),
                                            value: UserRole.Designer,
                                        },
                                    ]}
                                    onChange={(value) => setValue(field.name, value)}
                                    name={field.name}
                                    value={field.value}
                                />
                            )}
                        />
                    </Box>

                    {showMarketingOptIn && (
                        <Box paddingBottom="2bu">
                            <MarketingOptInInformationCard
                                userRole={watch('role') || UserRole.Customer}
                                headingSize="small"
                                {...register('marketingOptedIn')}
                            />
                        </Box>
                    )}

                    <ContinueButtonContainer paddingBottom="1bu">
                        <Button
                            loading={loading}
                            size="large"
                            width="fill"
                            type="submit"
                            disabled={watch('role') === undefined}
                        >
                            {__('Continue')}
                        </Button>
                    </ContinueButtonContainer>

                    <Box maxWidth={maxWidth} paddingBottom="1bu">
                        <PrivacyPolicyStatement openInNewTab />
                    </Box>
                </CenterStack>
            </form>
        </PageLayout>
    );
}

function WrappedNewUserByRoleSelectionPage() {
    return (
        <NewUserDetailsProvider>
            {(newUserDetails: SSONewUserDetails) => {
                return <NewUserByRoleSelectionPage newUserDetails={newUserDetails} />;
            }}
        </NewUserDetailsProvider>
    );
}

export default WrappedNewUserByRoleSelectionPage;
